<template>
    <v-row justify="end" dense>
        <ShippingLabelProductDialog
            :is-open="showProductDialog"
            :is-editable="isEditable"
            :product-data="productDialogData.product"
            :products="productDialogData.availableProducts"
            :is-ready-to-print="isReadyToPrint"
            :action="productDialogData.action"
            @on-save="confirmChangeProduct"
            @on-close="discardChangeProduct"
        />
        <ShippingLabelProductsDialog
            :is-open="showProductsDialog"
            :is-editable="isEditable"
            :configuration-products="productDialogData.availableProducts"
            :package-index="packageIndex"
            :position-index="positionIndex"
            @on-save="confirmChangeProducts"
            @on-close="discardChangeProduct"
        />

        <v-col cols="12" sm="10">
            <v-row align="center">
                <v-col cols="auto">
                    <strong>Towary</strong>
                </v-col>
                <v-col cols="auto">
                    <v-btn
                        v-if="availableProducts.length > 0 && (!isReadyToPrint || isEditable)"
                        color="secondary"
                        fab
                        x-small
                        class="elevation-1"
                        data-sentry-label="Dodaj towar"
                        :disabled="anyProductsLeft.length === 0"
                        @click="tryAddProduct"
                    >
                        <v-icon>
                            mdi-plus
                        </v-icon>
                    </v-btn>
                </v-col>
            </v-row>
            <v-data-table
                :items="items"
                :headers="packageHeaders"
                disable-pagination
                sort-by="goodName"
                :mobile-breakpoint="0"
                hide-default-footer
            >
                <template #item.name="{item}">
                    <v-skeleton-loader v-if="isLoading" type="table-cell" />
                    <span v-else :class="{'error--text': isError}">
                        {{ item.goodName }}
                    </span>
                </template>
                <template #item.amount="{item}">
                    <v-skeleton-loader v-if="isLoading" type="table-cell" />
                    <span v-else :class="{'error--text': isError}">
                        {{ item.amount }}
                    </span>
                </template>
                <template #item.unitPackageQuantity="{item}">
                    <v-skeleton-loader v-if="isLoading" type="table-cell" />
                    <span v-else :class="{'error--text': isError}">
                        {{ parseEmptyNumberValue(item.unitPackageQuantity) }} / {{ availableAmount(item) }} ({{ item.unitPackageType.name }})
                    </span>
                </template>
                <template #item.weight="{item}">
                    <v-skeleton-loader v-if="isLoading" type="table-cell" />
                    <span v-else :class="{'error--text': isError}">
                        {{ formatWeight(roundWeight(totalProductWeight(item))) }} / {{ formatWeight(roundWeight(totalProductWeight(item) + boxesWareWeight(item), 100)) }}
                    </span>
                </template>
                <template #item.actions="{item}">
                    <v-skeleton-loader v-if="isLoading" type="table-cell" />
                    <div v-else class="d-inline-flex">
                        <v-tooltip v-if="item.id" :open-delay="tooltipDelay" top>
                            <template #activator="{on}">
                                <v-btn
                                    icon
                                    data-sentry-label="Drukuj etykietę pakowania"
                                    :disabled="!isReadyToPrint || item.unitPackageType.id === 49 /* pakowanie luzem */"
                                    v-on="on"
                                    @click="printProduct(item)"
                                >
                                    <v-icon color="green">
                                        mdi-printer
                                    </v-icon>
                                </v-btn>
                            </template>
                            <span>Drukuj etykietę opakowaniową</span>
                        </v-tooltip>
                        <v-tooltip :open-delay="tooltipDelay" top>
                            <template #activator="{on}">
                                <v-btn
                                    v-if="isReadyToPrint"
                                    icon
                                    data-sentry-label="Otwórz"
                                    v-on="on"
                                    @click.native="showProduct(item)"
                                >
                                    <v-icon color="blue">
                                        mdi-ballot
                                    </v-icon>
                                </v-btn>
                            </template>
                            <span>Otwórz</span>
                        </v-tooltip>
                        <v-tooltip
                            v-if="!isReadyToPrint || isEditable"
                            :open-delay="tooltipDelay"
                            top
                        >
                            <template #activator="{on}">
                                <v-btn
                                    icon
                                    data-sentry-label="Edycja towaru"
                                    v-on="on"
                                    @click.native="tryEdit(item)"
                                >
                                    <v-icon color="blue">
                                        mdi-pencil
                                    </v-icon>
                                </v-btn>
                            </template>
                            <span>Edycja</span>
                        </v-tooltip>
                        <v-tooltip
                            v-if="!isReadyToPrint || isEditable"
                            :open-delay="tooltipDelay"
                            top
                        >
                            <template #activator="{on}">
                                <v-btn
                                    icon
                                    data-sentry-label="Usuń towar"
                                    v-on="on"
                                    @click.native="tryDelete(item)"
                                >
                                    <v-icon color="pink">
                                        mdi-delete
                                    </v-icon>
                                </v-btn>
                            </template>
                            <span>Usuń</span>
                        </v-tooltip>
                    </div>
                </template>
            </v-data-table>
        </v-col>
    </v-row>
</template>

<script>
import parse from '@/mixins/parse'

import ShippingLabelProductsDialog from './Dialogs/ShippingLabelProductsDialog.vue'
import ShippingLabelProductDialog from './Dialogs/ShippingLabelProductDialog.vue'
import tooltipDelay from '@/mixins/tooltip'
import weightFormatter from '@/mixins/weightFormatter'

export default {
    name: 'ShippingLabelProduct',
    components: {
        ShippingLabelProductDialog,
        ShippingLabelProductsDialog
    },
    mixins: [
        parse,
        weightFormatter,
        tooltipDelay,
    ],
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        products: {
            type: Array,
            default: () => [],
        },
        productsConfigurationList: {
            type: Array,
            default: () => [],
        },
        isError: {
            type: Boolean,
        },
        isLoading: {
            type: Boolean,
        },
        isReadyToPrint: {
            type: Boolean
        },
        isEditable: {
            type: Boolean,
        },
        packageIndex: {
            type: Number,
            default: null
        },
        positionIndex: {
            type: Number,
            default: null
        }

    },
    data() {
        return {
            showProductsDialog: false,
            showProductDialog: false,
            productDialogData: {
                product: {},
                availableProducts: [],
                action: null,
                onSave: () => {}
            },
            packageHeaders: [
                {
                    text: 'Towar',
                    value: 'name',
                    align: 'left',
                    sortable: false,
                    showOnRead: true
                },
                {
                    text: 'Nakład',
                    value: 'amount',
                    align: 'center',
                    sortable: false,
                    width: 150,
                    showOnRead: true
                },
                {
                    text: 'Ilość kartonów',
                    align: 'center',
                    sortable: false,
                    value: 'unitPackageQuantity',
                    showOnRead: true
                },
                {
                    text: 'Netto / Brutto',
                    value: 'weight',
                    align: 'center',
                    sortable: false,
                    width: 150,
                    showOnRead: true
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100,
                    showOnRead: false
                }
            ],
        }
    },
    computed: {
        isErrorHandle() {
            return this.isError
        },
        availableProducts() {
            return this.products.filter(product => !this.items.some(item => item.goodId === product.goodId))
        },
        anyProductsLeft() {
            return this.productsConfigurationList.filter(item => {
                return item.goodUnitPackageTypeId === 49 ? item.sourceGoodUnitTargetInPackageSum - item.assignedUnitTargetInPackageSumAmount !== 0 :
                    item.sourceGoodUnitPackageQuantity - item.assignedAmount !== 0
            }).filter(productsConfiguration => !productsConfiguration.shippingPositionPackagesIndexes.some(item =>
                item.addressIndex === this.positionIndex && item.packageIndex === this.packageIndex))
        },
    },
    methods: {
        addProduct(payload, products) {
            this.$emit('on-add', this.formattedProduct(payload), products)
        },
        editProduct(payload, configurationProduct) {
            this.$emit('on-edit', this.formattedProduct(payload), configurationProduct)
        },
        printProduct(item) {
            this.$emit('on-print', item)
        },
        tryAddProduct() {
            this.productDialogData.onSave = this.addProduct
            this.productDialogData.availableProducts = this.productsConfigurationList
            this.productDialogData.availableProducts.forEach(configurationProduct =>
                configurationProduct.goodUnitPackageTypeId === 49 ? configurationProduct.goodUnitTargetInPackageSum = configurationProduct.sourceGoodUnitTargetInPackageSum -
                configurationProduct.assignedUnitTargetInPackageSumAmount : configurationProduct.goodUnitPackageQuantity = configurationProduct.sourceGoodUnitPackageQuantity -
                 parseInt(configurationProduct.assignedAmount))
            this.showProductsDialog = true
        },
        tryEdit(item) {
            this.productDialogData.onSave = this.editProduct
            this.productDialogData.product = item
            this.productDialogData.action = 'edit'
            this.productDialogData.availableProducts = this.productsConfigurationList
            this.showProductDialog = true
        },
        showProduct(item) {
            this.productDialogData.product = item
            this.productDialogData.action = 'show'
            this.productDialogData.availableProducts = this.productsConfigurationList
            this.showProductDialog = true
        },
        tryDelete(item) {
            const configurationProductIndex = this.productsConfigurationList.findIndex(configurationProduct => configurationProduct.id === item.sourceConfigurationId)

            this.$emit('on-delete', item, this.productsConfigurationList[configurationProductIndex])
        },
        confirmChangeProducts(selectedProducts, products) {
            selectedProducts.forEach(product => this.productDialogData.onSave(product, products))

            this.productDialogData.product = {}
            this.productDialogData.availableProducts = []
        },
        confirmChangeProduct(addressProduct, configurationProduct) {
            this.productDialogData.onSave(addressProduct, configurationProduct)
            this.productDialogData.product = {}
            this.productDialogData.availableProducts = []
        },
        discardChangeProduct() {
            this.productDialogData.availableProducts = []
            this.productDialogData.action = null
            this.showProductsDialog = false
            this.showProductDialog = false
        },
        formattedProduct(payload) {
            return {
                ...payload,
                amount: payload.amount,
                unitPackageTypeId: payload.unitPackageTypeId,
            }
        },
        availableAmount(product) {
            const productPackageConfiguration = this.productsConfigurationList?.find(item => item.id === product.sourceConfigurationId)

            if (!product || !productPackageConfiguration) return '-'

            return productPackageConfiguration .goodUnitPackageTypeId === 49 ? 0 :
                productPackageConfiguration.sourceGoodUnitPackageQuantity - productPackageConfiguration.assignedAmount + product.unitPackageQuantity
        },
        totalProductWeight(product) {
            return product.good.singleItemWeight * product.amount
        },
        boxesWareWeight(product) {
            return product.unitPackageQuantity * product.unitPackageWeight
        },
    },
}
</script>